import { jsPDF } from 'jspdf';
import { autoTable } from 'jspdf-autotable';
import { fmtInt, fmtNumber, fmtScale, type OutputData, type RebarLineRow } from './helpers.ts';
import { filenameFor } from './exportCsv.ts';

const MARGIN = 14;

export function buildPdf(data: OutputData): jsPDF {
  const doc = new jsPDF({ unit: 'mm', format: 'letter', orientation: 'portrait' });

  drawCover(doc, data);

  if (data.civil) {
    doc.addPage();
    drawCivilSection(doc, data);
  }
  if (data.rebar) {
    doc.addPage();
    drawRebarSection(doc, data);
  }

  applyFooter(doc, data);
  return doc;
}

export function downloadPdf(data: OutputData): void {
  const doc = buildPdf(data);
  doc.save(filenameFor(data, 'pdf'));
}

// ─── Sections ────────────────────────────────────────────────────────────────

function drawCover(doc: jsPDF, data: OutputData): void {
  const pageW = doc.internal.pageSize.getWidth();

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(22);
  doc.text('Estimate Output', MARGIN, 28);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(11);
  doc.setTextColor(90);
  doc.text('Generated by OttoBid · ottobid.ai', MARGIN, 36);

  doc.setTextColor(0);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  doc.text(data.submission.job_name, MARGIN, 56);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(70);
  doc.text(`Source PDF: ${data.submission.source_pdf}`, MARGIN, 64);
  doc.text(`Prepared by: ${data.preparedBy}`, MARGIN, 70);
  doc.text(`Generated: ${data.generatedAt.toLocaleString()}`, MARGIN, 76);
  doc.setTextColor(0);

  // Summary table
  const rows: [string, string][] = [];
  if (data.civil) {
    rows.push(['Civil pages with content', String(data.civil.pages.length)]);
    rows.push(['Civil items', String(data.civil.totals.itemCount)]);
    rows.push(['Total linear', `${fmtNumber(data.civil.totals.linearLF)} LF`]);
    rows.push(['Total area', `${fmtInt(data.civil.totals.polygonSF)} SF`]);
    rows.push(['Total count', String(data.civil.totals.count)]);
  } else {
    rows.push(['Civil takeoff', 'no data']);
  }
  if (data.rebar) {
    rows.push(['Rebar segments', String(data.rebar.segmentCount)]);
    rows.push(['Rebar lines', String(data.rebar.lineCount)]);
    rows.push(['Rebar weight', `${fmtInt(data.rebar.totalLbs)} lb`]);
    rows.push(['Rebar tons', fmtNumber(data.rebar.totalTons, 2)]);
  } else {
    rows.push(['Rebar takeoff', 'no data']);
  }

  autoTable(doc, {
    startY: 92,
    head: [['Summary', '']],
    body: rows,
    theme: 'grid',
    margin: { left: MARGIN, right: MARGIN },
    headStyles: { fillColor: [40, 40, 50], textColor: 255, fontStyle: 'bold' },
    bodyStyles: { fontSize: 10 },
    columnStyles: {
      0: { cellWidth: 70, fontStyle: 'bold' },
      1: { cellWidth: pageW - MARGIN * 2 - 70 },
    },
  });
}

function drawCivilSection(doc: jsPDF, data: OutputData): void {
  if (!data.civil) return;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  doc.text('Civil Takeoff', MARGIN, 22);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(80);
  doc.text(
    `${data.civil.pages.length} page${data.civil.pages.length === 1 ? '' : 's'} · ${data.civil.totals.itemCount} items`,
    MARGIN,
    28,
  );
  doc.setTextColor(0);

  let y = 36;
  for (const page of data.civil.pages) {
    if (y > 250) {
      doc.addPage();
      y = 22;
    }
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.text(page.pageLabel, MARGIN, y);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(100);
    doc.text(`Scale: ${fmtScale(page.scale)}`, MARGIN, y + 5);
    doc.setTextColor(0);
    y += 9;

    if (page.rows.length === 0) {
      doc.setFontSize(9);
      doc.setTextColor(120);
      doc.text('(scale only · no items on this page)', MARGIN, y);
      doc.setTextColor(0);
      y += 8;
      continue;
    }

    autoTable(doc, {
      startY: y,
      head: [['Kind', 'Label', 'Quantity', 'Unit']],
      body: page.rows.map((r) => [
        r.kind,
        r.label,
        r.unit === 'EA' ? String(r.quantity) : fmtNumber(r.quantity),
        r.unit,
      ]),
      foot: [
        [
          { content: 'Page totals', colSpan: 2, styles: { fontStyle: 'bold', halign: 'right' } },
          buildPageTotalsCell(page.pageTotals),
          '',
        ],
      ],
      theme: 'striped',
      margin: { left: MARGIN, right: MARGIN },
      headStyles: { fillColor: [196, 155, 255], textColor: 30, fontStyle: 'bold' },
      footStyles: { fillColor: [240, 240, 245], textColor: 30 },
      bodyStyles: { fontSize: 9 },
    });
    y = (doc as unknown as { lastAutoTable: { finalY: number } }).lastAutoTable.finalY + 8;
  }

  // Grand totals table
  if (y > 240) {
    doc.addPage();
    y = 22;
  }
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.text('Civil Grand Totals', MARGIN, y);
  autoTable(doc, {
    startY: y + 4,
    head: [['Metric', 'Total']],
    body: [
      ['Linear', `${fmtNumber(data.civil.totals.linearLF)} LF`],
      ['Area', `${fmtInt(data.civil.totals.polygonSF)} SF`],
      ['Count', String(data.civil.totals.count)],
    ],
    theme: 'grid',
    margin: { left: MARGIN, right: MARGIN },
    headStyles: { fillColor: [60, 60, 75], textColor: 255 },
    bodyStyles: { fontSize: 10 },
  });
}

function buildPageTotalsCell(t: { linearLF: number; polygonSF: number; count: number }): string {
  const parts: string[] = [];
  if (t.linearLF > 0) parts.push(`${fmtNumber(t.linearLF)} LF`);
  if (t.polygonSF > 0) parts.push(`${fmtInt(t.polygonSF)} SF`);
  if (t.count > 0) parts.push(`${t.count} EA`);
  return parts.length > 0 ? parts.join(' · ') : '—';
}

function drawRebarSection(doc: jsPDF, data: OutputData): void {
  if (!data.rebar) return;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  doc.text('Rebar Takeoff', MARGIN, 22);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(80);
  doc.text(
    `${data.rebar.segmentCount} segments · ${data.rebar.lineCount} lines · ${fmtInt(data.rebar.totalLbs)} lb (${fmtNumber(data.rebar.totalTons, 2)} tons)`,
    MARGIN,
    28,
  );
  doc.setTextColor(0);

  // Weight summary by size
  autoTable(doc, {
    startY: 36,
    head: [['Size', 'Lbs', 'Tons', 'Bar Count', 'Total LF']],
    body: data.rebar.weightSummary.map((e) => [
      e.size.toString(),
      fmtInt(e.total),
      fmtNumber(e.tons, 3),
      String(e.barCount),
      fmtNumber(e.totalLinealFeet),
    ]),
    foot: [
      [
        { content: 'TOTAL', styles: { fontStyle: 'bold' } },
        { content: fmtInt(data.rebar.totalLbs), styles: { fontStyle: 'bold' } },
        { content: fmtNumber(data.rebar.totalTons, 3), styles: { fontStyle: 'bold' } },
        '',
        '',
      ],
    ],
    theme: 'grid',
    margin: { left: MARGIN, right: MARGIN },
    headStyles: { fillColor: [0, 232, 122], textColor: 30 },
    footStyles: { fillColor: [220, 240, 230], textColor: 30 },
    bodyStyles: { fontSize: 10 },
  });

  let y = (doc as unknown as { lastAutoTable: { finalY: number } }).lastAutoTable.finalY + 10;

  // Line items — grouped by segment with subtotal rows interleaved
  if (y > 240) {
    doc.addPage();
    y = 22;
  }
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.text('Line Items', MARGIN, y);

  const subtotalRowIndices = new Set<number>();
  const lineItemBody: string[][] = [];
  let currentSegment: string | null = null;
  let groupRows: RebarLineRow[] = [];

  for (const r of data.rebar.lineRows) {
    if (currentSegment !== null && r.segmentName !== currentSegment) {
      const subtotalLF = groupRows.reduce((sum, gr) => sum + gr.totalLengthFt, 0);
      const subtotalLbs = groupRows.reduce((sum, gr) => sum + gr.totalWeightLbs, 0);
      subtotalRowIndices.add(lineItemBody.length);
      lineItemBody.push([currentSegment, '', '', '', '', '', '', '', '', fmtNumber(subtotalLF, 2), fmtInt(subtotalLbs)]);
      groupRows = [];
    }
    currentSegment = r.segmentName;
    lineItemBody.push([
      r.segmentName,
      String(r.lineNumber),
      r.material,
      r.function,
      r.size,
      r.class,
      String(r.quantity),
      String(r.multiplier),
      fmtNumber(r.unitLengthFt, 2),
      fmtNumber(r.totalLengthFt, 2),
      fmtInt(r.totalWeightLbs),
    ]);
    groupRows.push(r);
  }
  if (groupRows.length > 0 && currentSegment !== null) {
    const subtotalLF = groupRows.reduce((sum, gr) => sum + gr.totalLengthFt, 0);
    const subtotalLbs = groupRows.reduce((sum, gr) => sum + gr.totalWeightLbs, 0);
    subtotalRowIndices.add(lineItemBody.length);
    lineItemBody.push([currentSegment, '', '', '', '', '', '', '', '', fmtNumber(subtotalLF, 2), fmtInt(subtotalLbs)]);
  }

  autoTable(doc, {
    startY: y + 4,
    head: [['Segment', 'Line', 'Mat', 'Fn', 'Size', 'Cls', 'Qty', 'Mult', 'Unit (ft)', 'Total (ft)', 'Wt (lb)']],
    body: lineItemBody,
    theme: 'striped',
    margin: { left: MARGIN, right: MARGIN },
    headStyles: { fillColor: [40, 40, 50], textColor: 255 },
    bodyStyles: { fontSize: 8 },
    styles: { cellPadding: 1.5 },
    didParseCell: (hookData) => {
      if (hookData.section === 'body' && subtotalRowIndices.has(hookData.row.index)) {
        hookData.cell.styles.fontStyle = 'bold';
        hookData.cell.styles.fillColor = [220, 240, 228];
        hookData.cell.styles.textColor = [30, 120, 60];
      }
    },
  });
}

function applyFooter(doc: jsPDF, data: OutputData): void {
  const total = doc.getNumberOfPages();
  for (let i = 1; i <= total; i++) {
    doc.setPage(i);
    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(140);
    doc.text(
      `${data.submission.job_name} · ${data.generatedAt.toLocaleDateString()}`,
      MARGIN,
      pageH - 8,
    );
    doc.text(`Page ${i} of ${total}`, pageW - MARGIN, pageH - 8, { align: 'right' });
    doc.setTextColor(0);
  }
}
